/* One deliberate agreement per distinct license, for the current cart only. */
if (!customElements.get('tt-cart-license')) {
  customElements.define('tt-cart-license', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected || this.controller) return;
      this.form = document.getElementById(this.dataset.formId);
      this.checkboxes = [...this.querySelectorAll('[data-tt-license]')];
      this.message = this.querySelector('[data-license-message]');
      this.record = this.querySelector('[data-license-record]');
      this.itemsRecord = this.querySelector('[data-license-items]');
      this.lines = JSON.parse(this.querySelector('[data-cart-lines]').textContent);
      if (!this.form || !this.checkboxes.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const captureOptions = { ...options, capture: true };
      this.reset();
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-tt-license]')) this.update();
      }, options);
      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-tt-license-checkout]');
        if (button?.form?.id !== this.dataset.formId) return;
        if (!this.accepted()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showMissing();
        }
      }, captureOptions);
      document.addEventListener('invalid', (event) => {
        if (this.contains(event.target) && event.target.matches('[data-tt-license]')) {
          event.preventDefault(); // Prevent native validation from scrolling after our highlight.
          this.showMissing();
        }
      }, captureOptions);
      document.addEventListener('submit', (event) => {
        if (event.target.id !== this.dataset.formId) return;
        this.form = event.target;
        this.submit(event);
      }, captureOptions);
      window.addEventListener('pageshow', () => this.reset(), options);
      document.addEventListener('reset', (event) => {
        if (event.target.id === this.dataset.formId) queueMicrotask(() => this.reset());
      }, options);
      this.buttons().forEach((button) => { button.disabled = false; });
    }

    buttons() {
      return [...document.querySelectorAll('[data-tt-license-checkout]')]
        .filter((button) => button.form?.id === this.dataset.formId);
    }

    accepted() {
      return this.checkboxes.every((input) => input.checked && !input.disabled);
    }

    reset() {
      this.checkboxes.forEach((input) => { input.checked = false; });
      this.record.value = '';
      this.itemsRecord.value = '';
      this.update();
    }

    update() {
      this.classList.remove('tt-cart-license--error');
      this.checkboxes.forEach((input) => input.removeAttribute('aria-invalid'));
      this.message.textContent = '';
      this.classList.toggle('tt-cart-license--accepted', this.accepted());
      // No acceptance is persisted by checking alone, only with checkout submission.
      this.record.value = '';
      this.itemsRecord.value = '';
    }

    showMissing() {
      this.classList.add('tt-cart-license--error');
      const missing = this.checkboxes.filter((input) => !input.checked || input.disabled);
      missing.forEach((input) => input.setAttribute('aria-invalid', 'true'));
      this.message.textContent = missing.some((input) => input.disabled)
        ? 'The license terms could not be loaded. Please reload this page or contact me before checkout.'
        : 'Accept the license' + (missing.length > 1 ? 's' : '') + ' to continue.';
      const input = missing.find((item) => !item.disabled);
      const target = input?.closest('label') || this.message;
      const rect = target.getBoundingClientRect();
      const headerBottom = document.querySelector('sticky-header')?.getBoundingClientRect().bottom || 0;
      const viewport = window.visualViewport;
      const visibleTop = Math.max(viewport?.offsetTop || 0, headerBottom) + 8;
      const visibleBottom = (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight) - 8;
      // Highlight in place. Only reveal the checkbox when it is actually off screen.
      if (rect.top < visibleTop || rect.bottom > visibleBottom) {
        target.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }
      input?.focus({ preventScroll: true });
    }

    signature(lines) {
      return JSON.stringify(lines.map((item) => [item.key, item.quantity])
        .sort((a, b) => a[0].localeCompare(b[0])));
    }

    async submit(event) {
      if (this.readyToSubmit) {
        this.readyToSubmit = false;
        return;
      }
      // Cart quantity/update submissions are unrelated to checkout.
      if (event.submitter && event.submitter.name !== 'checkout') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!this.accepted()) return this.showMissing();
      if (this.busy) return;
      this.busy = true;
      this.buttons().forEach((button) => { button.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      this.message.textContent = 'Checking your cart…';
      const controller = new AbortController();
      this.verificationController = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(this.dataset.cartUrl + '.js', {
          credentials: 'same-origin', cache: 'no-store', signal: controller.signal
        });
        if (!response.ok) throw new Error('Cart check failed');
        const currentCart = await response.json();
        if (!this.isConnected) return;
        if (this.signature(currentCart.items) !== this.signature(this.lines)) {
          this.reset();
          this.classList.add('tt-cart-license--error');
          this.message.textContent = 'Your cart changed. Please reload your cart, review the items and accept their licenses.';
          const link = document.createElement('a');
          link.href = this.dataset.cartUrl;
          link.textContent = ' Reload cart';
          this.message.append(link);
          this.scrollIntoView({ block: 'center', behavior: 'auto' });
          return;
        }
        if (!this.accepted()) return this.showMissing();
        const agreements = this.checkboxes.map((input) => ({
          license: input.dataset.licenseName,
          terms: '/pages/' + input.dataset.licenseHandle,
          version: input.dataset.licenseVersion
        }));
        this.record.value = JSON.stringify({ accepted_at: new Date().toISOString(), agreements });
        this.itemsRecord.value = this.signature(this.lines);
        this.message.textContent = '✓ Licenses accepted. Opening checkout…';
        const button = event.submitter || this.buttons()[0];
        this.buttons().forEach((item) => { item.disabled = false; });
        this.readyToSubmit = true;
        this.form.requestSubmit(button);
      } catch (error) {
        if (!this.isConnected) return;
        this.readyToSubmit = false;
        this.record.value = '';
        this.itemsRecord.value = '';
        this.classList.add('tt-cart-license--error');
        this.message.textContent = 'I couldn’t check your cart. Your items are safe. Please try checkout again.';
      } finally {
        clearTimeout(timeout);
        this.verificationController = null;
        this.busy = false;
        this.removeAttribute('aria-busy');
        if (this.isConnected) this.buttons().forEach((button) => { button.disabled = false; });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.verificationController?.abort();
      this.controller = null;
    }
  });
}
